<template>
  <b-card title="Delete Account" class="mt-5" style="width: 550px">
    <b-form onkeydown="return event.key != 'Enter'">
      <!-- ── Primary BA: replacement selection step ── -->
      <template v-if="isPrimaryBa">
        <div v-if="loadingBas" class="text-center py-3">
          <b-spinner small /> Loading team members…
        </div>

        <template v-else>
          <!-- Case 1: other BAs exist — must pick a replacement -->
          <template v-if="otherBas.length > 0">
            <p class="pb-1 grey--text">
              As the primary Business Admin you must select a replacement before
              deleting your account. The selected team member will become the
              new primary Business Admin immediately.
            </p>
            <p class="pb-2 grey--text">
              Your account will enter a
              <strong>30-day retention period</strong>. You can restore it (as a
              non-primary BA) by logging back in before it expires.
            </p>

            <b-form-group
              label="Select new primary Business Admin"
              label-for="select-replacement"
            >
              <b-form-select
                id="select-replacement"
                v-model="replacementBaUserId"
                :options="replacementOptions"
                :state="replacementState"
              />
              <b-form-invalid-feedback
                >Please select a replacement.</b-form-invalid-feedback
              >
            </b-form-group>
          </template>

          <!-- Case 2: no other BAs — warn and proceed -->
          <template v-else>
            <b-alert show variant="warning" class="mb-3">
              <strong>No other Business Admins found.</strong><br />
              Your account and company will be placed on a
              <strong>30-day retention period</strong>. To restore access you
              will need to contact
              <a href="mailto:support@tradiepass.com.au"
                >support@tradiepass.com.au</a
              >. After 30 days your account and all company data will be
              permanently deleted.
            </b-alert>
          </template>
        </template>
      </template>

      <!-- ── Non-primary BA: 30-day retention ── -->
      <template v-else-if="isBusinessAdmin">
        <p class="pb-2 grey--text">
          Your account will enter a <strong>30-day retention period</strong>.
          You lose access immediately, but you can restore it by logging back in
          before it expires. After 30 days it is permanently deleted.
        </p>
      </template>

      <!-- ── Tradie: one-week notice period if they have a company, else straight away ── -->
      <template v-else>
        <p v-if="companies.length" class="pb-2 grey--text">
          Are you sure you want to delete your account?
          <br />Your account and all details will be permanently deleted after
          <strong>1 week</strong>. You can cancel by logging back in before
          then.
        </p>
        <p
          v-else-if="companiesLoaded"
          class="pb-2 grey--text"
          data-testid="delete-account-immediate-warning"
        >
          Are you sure you want to delete your account?
          <br />You aren't associated with a company, so your account and all
          details will be permanently deleted <strong>straight away</strong>.
          This can't be undone.
        </p>
        <b-alert
          v-if="companies.length"
          show
          variant="warning"
          class="mb-3"
          data-testid="delete-account-company-warning"
        >
          You are associated with
          <strong>{{ companyNames }}</strong
          >. The company will be notified and given the week to review your
          files. Some company records may be retained by the company where
          required or permitted by law or for legitimate business purposes.
        </b-alert>
      </template>

      <!-- Password confirmation (always shown once BAs are loaded) -->
      <template v-if="!isPrimaryBa || !loadingBas">
        <b-form-group
          id="input-group-password"
          label="Enter your current password to continue"
          label-for="input-password"
        >
          <b-form-input
            id="input-password"
            v-model="form.password"
            type="password"
            required
            placeholder="Enter password"
            :state="state.password"
            aria-describedby="input-password-feedback"
            @keyup.enter="generateToken"
          />
          <b-form-invalid-feedback id="input-password-feedback">
            {{ errors.password }}
          </b-form-invalid-feedback>
        </b-form-group>

        <div class="d-flex justify-content-between">
          <b-button
            variant="outline-secondary"
            :to="{ name: 'user.settings-page', hash: '#account' }"
            >Cancel</b-button
          >
          <b-button
            variant="outline-danger"
            :disabled="
              loading ||
              (isPrimaryBa && otherBas.length > 0 && !replacementBaUserId)
            "
            @click="generateToken"
          >
            <b-spinner v-if="loading" small label="Loading" />&nbsp;Delete
          </b-button>
        </div>
      </template>
    </b-form>
  </b-card>
</template>

<script>
import { mapState, mapActions } from 'vuex'

export default {
  computed: {
    ...mapState({
      user: (state) => state.auth.user,
      verificationToken: (state) => state.auth.verificationToken,
    }),

    isPrimaryBa() {
      return (
        this.user &&
        this.user.user_type === 'business_admin' &&
        !!this.user.is_primary
      )
    },

    isBusinessAdmin() {
      return this.user && this.user.user_type === 'business_admin'
    },

    companyNames() {
      return this.companies.map((c) => c.name).join(', ')
    },

    replacementOptions() {
      return [
        { value: null, text: '- Select a team member -' },
        ...this.otherBas.map((ba) => ({
          value: ba.id,
          text: ba.full_name || ba.email || `User #${ba.id}`,
        })),
      ]
    },
  },

  data() {
    return {
      loading: false,
      loadingBas: false,
      companies: [],
      // Until the company list has loaded we can't say which copy applies.
      companiesLoaded: false,
      otherBas: [],
      replacementBaUserId: null,
      replacementState: null,
      form: {
        password: null,
      },
      state: {
        password: null,
      },
      errors: {
        password: null,
      },
    }
  },

  methods: {
    ...mapActions({
      getVerificationToken: 'auth/getVerificationToken',
      deleteAccount: 'auth/deleteAccount',
    }),

    async fetchOtherBas() {
      this.loadingBas = true
      try {
        const { data } = await this.$api.get('company/business-admins', {
          params: { search: '', archived_only: false },
        })
        const items = data.data || data
        this.otherBas = items.filter(
          (ba) => !ba.is_primary && ba.id !== this.user.id,
        )
      } catch {
        this.otherBas = []
      } finally {
        this.loadingBas = false
      }
    },

    // Tradies only: which companies will be told about the deletion.
    async fetchCompanies() {
      try {
        const { data } = await this.$api.get('companies/associations')
        this.companies = (data && data.data) || []
      } catch {
        this.companies = []
      } finally {
        this.companiesLoaded = true
      }
    },

    async generateToken() {
      if (
        this.isPrimaryBa &&
        this.otherBas.length > 0 &&
        !this.replacementBaUserId
      ) {
        this.replacementState = false
        return
      }

      this.loading = true
      await this.getVerificationToken({ password: this.form.password })
        .then(() => this.removeAccount())
        .catch((error) => {
          if (error.response && error.response.data) {
            this.state.password = false
            this.errors.password = error.response.data.message
          }
        })
      this.loading = false
    },

    async removeAccount() {
      this.loading = true
      await this.deleteAccount({
        replacementBaUserId: this.replacementBaUserId,
        scheduled: !this.isBusinessAdmin,
      })
      this.loading = false
    },
  },

  watch: {
    'form.password'() {
      this.state.password = !!(
        this.form.password && this.form.password.length !== 0
      )
      this.errors.password = 'This field is required'
    },

    replacementBaUserId(val) {
      if (val) this.replacementState = null
    },
  },

  async created() {
    if (this.isPrimaryBa) {
      await this.fetchOtherBas()
    } else if (!this.isBusinessAdmin) {
      await this.fetchCompanies()
    }
  },
}
</script>

<style lang="scss" scoped>
.btn {
  height: 36px;
  padding: 4px 32px;
}
</style>
